import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

// Render a component directly (no API mocking needed — these are prop-driven components)
async function renderComponent(element, assertions, waitMs = 200) {
  const logCapture = createLogCapture();
  let snapshot = null;
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

// ─── Mock fragment fixtures ──────────────────────────────────────────────────

const PROCESSING_FRAGMENT = {
  uuid: 'frag-processing-1',
  status: 'processing',
  result: null,
  speaker_names: null,
};

// AudioEmotionAnalysisCard expects overall_results.segments with emotion data
const COMPLETED_AUDIO_FRAGMENT = {
  uuid: 'frag-audio-1',
  status: 'completed',
  result: JSON.stringify({
    overall_results: {
      segments: [
        { dominant_emotion: 'happiness', confidence: 0.8, emotions: { happiness: 0.80, neutral: 0.15, sadness: 0.05 }, start_time: 0, end_time: 3 },
        { dominant_emotion: 'happiness', confidence: 0.7, emotions: { happiness: 0.65, neutral: 0.25, fear: 0.10 }, start_time: 3, end_time: 6 },
        { dominant_emotion: 'neutral',   confidence: 0.6, emotions: { happiness: 0.30, neutral: 0.60, sadness: 0.10 }, start_time: 6, end_time: 9 },
      ],
      audio_info: { duration: 9 },
      time_series: [],
    },
  }),
  speaker_names: JSON.stringify(['Alice Test', 'Bob Test']),
};

// VideoEmotionAnalysisCard expects summary.dominant_emotion + frames object
const COMPLETED_VIDEO_FRAGMENT = {
  uuid: 'frag-video-1',
  status: 'completed',
  result: JSON.stringify({
    summary: { dominant_emotion: 'happiness', emotions_detected: true },
    frames: {
      '0.0': { timestamp: 0.0, emotion_scores: { happiness: 0.75, sadness: 0.05, neutral: 0.20 }, action_units: {} },
      '0.5': { timestamp: 0.5, emotion_scores: { happiness: 0.65, sadness: 0.10, neutral: 0.25 }, action_units: {} },
      '1.0': { timestamp: 1.0, emotion_scores: { happiness: 0.80, sadness: 0.05, neutral: 0.15 }, action_units: {} },
    },
    video_info: { duration: 1.0 },
  }),
  speaker_names: null,
};

const SENTIMENT_FRAGMENT = {
  uuid: 'frag-sentiment-1',
  status: 'completed',
  result: JSON.stringify({ Positive: '55.0', Negative: '20.0', Neutral: '25.0' }),
};

// Audio emotion fragment with proper emotion_distribution format
const AUDIO_EMOTION_DISTRIBUTION_FRAGMENT = {
  uuid: 'frag-audio-emotion-1',
  status: 'completed',
  result: JSON.stringify({
    overall_results: {
      emotion_distribution: {
        happiness: 0.45,
        sadness: 0.20,
        anger: 0.10,
        fear: 0.05,
        surprise: 0.05,
        disgust: 0.05,
        neutral: 0.10,
      },
      total_duration_seconds: 120,
      total_segments: 40,
    },
  }),
};

// Audio emotion fragment with time series data for the timeline card
const AUDIO_TIMELINE_FRAGMENT = {
  uuid: 'frag-timeline-1',
  status: 'completed',
  result: JSON.stringify({
    time_series_results: [
      { second: 0, dominant_emotion: 'happiness', emotions: { happiness: 0.7, neutral: 0.2, sadness: 0.1 } },
      { second: 3, dominant_emotion: 'neutral', emotions: { happiness: 0.3, neutral: 0.6, sadness: 0.1 } },
      { second: 6, dominant_emotion: 'happiness', emotions: { happiness: 0.8, neutral: 0.1, sadness: 0.1 } },
      { second: 9, dominant_emotion: 'sadness', emotions: { happiness: 0.2, neutral: 0.3, sadness: 0.5 } },
    ],
  }),
};

// Audio emotion fragment without time series (shows "no timeline data" state)
const AUDIO_NO_TIMELINE_FRAGMENT = {
  uuid: 'frag-no-timeline-1',
  status: 'completed',
  result: JSON.stringify({
    overall_results: {
      emotion_distribution: { happiness: 0.6, neutral: 0.4 },
    },
  }),
};

// Mock translation helpers used by some emotion cards
const mockT = (key, fallback) => fallback || key;
const mockSentimentTranslations = (key) => key;
const mockMapSentimentAndColors = (key) => {
  const map = { Engagement: 'green-600', Frustration: 'red-600', Sadness: 'blue-400', Neutral: 'gray-400', Positive: 'green-600', Negative: 'red-600' };
  return map[key] || 'gray-400';
};

export const emotionAnalysisTests = [

  // ─── AudioEmotionAnalysisCard ────────────────────────────────────────────────

  {
    id: 'audio-emotion-card-processing',
    name: 'AudioEmotionAnalysisCard: shows card in processing state',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={PROCESSING_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card !== null, 'emotion-analysis-card must render for processing fragment');
        }
      );
    },
  },

  {
    id: 'audio-emotion-card-completed',
    name: 'AudioEmotionAnalysisCard: renders with completed emotion data',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={COMPLETED_AUDIO_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card !== null, 'emotion-analysis-card must render for completed fragment');
        }
      );
    },
  },

  {
    id: 'audio-emotion-card-null-fragment',
    name: 'AudioEmotionAnalysisCard: renders nothing when fragment is null',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={null}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card === null, 'emotion-analysis-card must NOT render when fragment is null');
        }
      );
    },
  },

  // ─── VideoEmotionAnalysisCard ────────────────────────────────────────────────

  {
    id: 'video-emotion-card-processing',
    name: 'VideoEmotionAnalysisCard: shows card in processing state',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={PROCESSING_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card !== null, 'video-emotion-analysis-card must render for processing fragment');
        }
      );
    },
  },

  {
    id: 'video-emotion-card-completed',
    name: 'VideoEmotionAnalysisCard: renders with completed video emotion data',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={COMPLETED_VIDEO_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card !== null, 'video-emotion-analysis-card must render for completed fragment');
        }
      );
    },
  },

  {
    id: 'video-emotion-card-null-fragment',
    name: 'VideoEmotionAnalysisCard: renders nothing when fragment is null',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={null}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card === null, 'video-emotion-analysis-card must NOT render when fragment is null');
        }
      );
    },
  },

  // ─── OverallSentimentCard ────────────────────────────────────────────────────

  {
    id: 'overall-sentiment-card-renders',
    name: 'OverallSentimentCard: renders with sentiment result data',
    run: async () => {
      const OverallSentimentCard = require('../../Components/NewAnalysisElements/OverallSentimentCard').default;
      return await renderComponent(
        withWrapper(
          <OverallSentimentCard
            fragment={SENTIMENT_FRAGMENT}
            t={mockT}
            sentimentTranslations={mockSentimentTranslations}
            mapSentimentAndColors={mockMapSentimentAndColors}
            speakerNames={[]}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="overall-sentiment-card"]');
          assert(card !== null, 'overall-sentiment-card must render');
        }
      );
    },
  },

  // ─── OverallParticipationCard ─────────────────────────────────────────────────

  {
    id: 'overall-participation-card-renders',
    name: 'OverallParticipationCard: renders with participation data',
    run: async () => {
      const OverallParticipationCard = require('../../Components/NewAnalysisElements/OverallParticipationCard').default;
      const participationFragment = {
        uuid: 'frag-participation-1',
        status: 'completed',
        result: JSON.stringify({ 'Alice Test': '60.5', 'Bob Test': '39.5' }),
        speaker_names: JSON.stringify(['Alice Test', 'Bob Test']),
      };
      return await renderComponent(
        withWrapper(
          <OverallParticipationCard
            fragment={participationFragment}
            t={mockT}
            duration={7230}
            speakerNames={[]}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="overall-participation-card"]');
          assert(card !== null, 'overall-participation-card must render');
        }
      );
    },
  },

  // ─── AudioEmotionSentimentCard ────────────────────────────────────────────────

  {
    id: 'audio-sentiment-card-not-ready',
    name: 'AudioEmotionSentimentCard: renders not-ready state when fragment incomplete',
    run: async () => {
      const AudioEmotionSentimentCard = require('../../Components/NewAnalysisElements/AudioEmotionSentimentCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionSentimentCard fragment={PROCESSING_FRAGMENT} />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="audio-emotion-sentiment-card"]');
          assert(card !== null, 'audio-emotion-sentiment-card must render in not-ready state');
        }
      );
    },
  },

  {
    id: 'audio-sentiment-card-with-emotions',
    name: 'AudioEmotionSentimentCard: renders with audio emotion distribution data',
    run: async () => {
      const AudioEmotionSentimentCard = require('../../Components/NewAnalysisElements/AudioEmotionSentimentCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionSentimentCard fragment={AUDIO_EMOTION_DISTRIBUTION_FRAGMENT} />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="audio-emotion-sentiment-card"]');
          assert(card !== null, 'audio-emotion-sentiment-card must render with emotion distribution data');
        }
      );
    },
  },

  // ─── AudioEmotionSentimentTimelineCard ───────────────────────────────────────

  {
    id: 'audio-timeline-card-not-ready',
    name: 'AudioEmotionSentimentTimelineCard: renders not-ready state when fragment not completed',
    run: async () => {
      const AudioEmotionSentimentTimelineCard = require('../../Components/NewAnalysisElements/AudioEmotionSentimentTimelineCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionSentimentTimelineCard fragment={PROCESSING_FRAGMENT} />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="audio-emotion-sentiment-timeline-card"]');
          assert(card !== null, 'audio-emotion-sentiment-timeline-card must render in not-ready state');
        }
      );
    },
  },

  {
    id: 'audio-timeline-card-no-data',
    name: 'AudioEmotionSentimentTimelineCard: renders no-timeline state when result has no time series',
    run: async () => {
      const AudioEmotionSentimentTimelineCard = require('../../Components/NewAnalysisElements/AudioEmotionSentimentTimelineCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionSentimentTimelineCard fragment={AUDIO_NO_TIMELINE_FRAGMENT} />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="audio-emotion-sentiment-timeline-card"]');
          assert(card !== null, 'audio-emotion-sentiment-timeline-card must render when no time series data');
        }
      );
    },
  },

  {
    id: 'audio-timeline-card-with-data',
    name: 'AudioEmotionSentimentTimelineCard: renders chart when time_series_results provided',
    run: async () => {
      const AudioEmotionSentimentTimelineCard = require('../../Components/NewAnalysisElements/AudioEmotionSentimentTimelineCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionSentimentTimelineCard fragment={AUDIO_TIMELINE_FRAGMENT} />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="audio-emotion-sentiment-timeline-card"]');
          assert(card !== null, 'audio-emotion-sentiment-timeline-card must render with time series data');
        }
      );
    },
  },

];

